import * as S from './input.styles';
import { InputIconProps, InputProps } from './input.types';
import { Icon } from '../icon/Icon';
import { IconButton } from '@design-system/atoms/icon-button';
import { forwardRef, useState } from 'react';
import { iconPaths } from '../icon';
import { InputMessage } from '../input-message';
import DatePicker from './DatePicker';
import DateTimePicker from './DateTimePicker';

/**
 * InputIcon component
 * @param props - InputIconProps
 * @returns InputIcon component
 */

const InputIcon = (props: InputIconProps) => {
  const { icon, onPress } = props;

  if (onPress && typeof onPress === 'function') {
    return <IconButton icon={icon} size={20} onPress={onPress} data-testid={`icon-button-${icon}`} />;
  }
  return <Icon name={icon} size={20} data-testid={`icon-${icon}`} />;
};

InputIcon.displayName = 'InputIcon';

/**
 * Input component
 * @param props - InputProps
 * @returns Input component
 */

export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const {
    label,
    labelHint,
    error,
    hint,
    isTtlField,
    startIcon: StartIconProp,
    endIcon,
    onPressStartIcon,
    onPressEndIcon,
    className,
    classNames,
    onBlur,
    variant = 'default',
    disabled = false,
    containerTestId,
    ...inputProps
  } = props;
  const [isFocused, setIsFocused] = useState(false);

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    onBlur?.(event);
  };

  const value = inputProps.value ?? '';
  const valueLength = String(value).length;

  return (
    <S.InputWrapper className={className} data-testid="input-wrapper">
      {label && (
        <S.LabelRow>
          <S.LabelText className={classNames?.label}>
            {label}
            {/* {labelHint && <S.LabelHint as="span">{labelHint}</S.LabelHint>} */}
          </S.LabelText>
          {inputProps.maxLength && props.showCharacterCount && (
            <S.CharacterCount>
              {valueLength}/{inputProps.maxLength}
            </S.CharacterCount>
          )}
        </S.LabelRow>
      )}
      <S.InputContainer
        hasError={Boolean(error)}
        hasHint={Boolean(hint)}
        isFocused={isFocused}
        variant={variant}
        data-testid={containerTestId ?? 'input-container'}
        data-focused={isFocused}
        disabled={disabled}
        isTtlField={isTtlField}
      >
        {StartIconProp &&
          (typeof StartIconProp === 'string' ? (
            <InputIcon icon={StartIconProp as keyof typeof iconPaths} onPress={onPressStartIcon} />
          ) : (
            StartIconProp
          ))}
        {inputProps.type === 'date' ? (
          <DatePicker disabled={disabled} {...inputProps} />
        ) : inputProps.type === 'datetime-local' ? (
          <DateTimePicker disabled={disabled} {...inputProps} />
        ) : (
          <S.Input
            disabled={disabled}
            ref={ref}
            isTtlField={isTtlField}
            onFocus={() => setIsFocused(true)}
            onBlur={handleBlur}
            {...inputProps}
          />
        )}
        {endIcon ? <InputIcon icon={endIcon} onPress={onPressEndIcon} /> : null}
      </S.InputContainer>
      <InputMessage hint={hint} error={error} />
    </S.InputWrapper>
  );
});

Input.displayName = 'Input';
